import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Text,
  Card,
  LegacyCard,
  ResourceList,
  ResourceItem,
  Avatar,
  TextField,
  Button,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import useIsPhone from "features/files/use-is-phone";
import { fetchUsers } from "features/users/users.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";

// A phone shows this many people at a time; a company of hundreds is not a page to scroll through.
const PAGE_SIZE = 30;

// ResourceItem lays an invisible button over the whole row; a link inside needs to sit above it to be tapped.
const linkAboveItem = { position: "relative", zIndex: 2 };

function UserList({ source }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { companyId } = useParams();
  const { usersCollection, isLoading } = useSelector(
    (state) => state.usersReducer
  );
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    dispatch(fetchUsers(companyId, "", source));
  }, [dispatch, companyId, source]);

  // Another company is another list: it starts from its first page with no query.
  useEffect(() => {
    setSearch("");
    setVisibleCount(PAGE_SIZE);
  }, [companyId]);

  // The whole company is already loaded, so the search filters it here, by name, e-mail or phone.
  // A computer shows everyone with no search, as it always did.
  const query = isPhone ? search.trim().toLowerCase() : "";
  const matchingUsers = query
    ? usersCollection.filter(({ name, email, phone }) =>
        [name, email, phone].some((value) => value && value.toLowerCase().includes(query))
      )
    : usersCollection;
  const shownUsers = isPhone ? matchingUsers.slice(0, visibleCount) : matchingUsers;

  const handleSearch = (value) => {
    setSearch(value);
    setVisibleCount(PAGE_SIZE);
  };

  // The same sheet the online-users panel opens for a person.
  const openProfile = (userId) => {
    dispatch(setModalxComponent("UserPublicProfileModal"));
    dispatch(setModalxComponentProps({ companyId, userId }));
    dispatch(setIsModalxOpen(true));
  };

  return (
    <Card>
      <div style={{ marginBottom: "10px" }}>
        <Text as="h4" fontWeight="bold" variant="headingLg" alignment="start">
          {t("users")}
        </Text>
      </div>

      {isPhone && (
        <div style={{ marginBottom: "10px" }}>
          <TextField
            label={t("search")}
            labelHidden
            placeholder={t("search")}
            value={search}
            onChange={handleSearch}
            clearButton
            onClearButtonClick={() => handleSearch("")}
            autoComplete="off"
          />
        </div>
      )}

      <LegacyCard>
        <ResourceList
          resourceName={{ singular: "user", plural: "users" }}
          items={shownUsers}
          loading={isLoading}
          renderItem={(item) => {
            const { id, name, phone, email } = item;
            return (
              <ResourceItem
                verticalAlignment="center"
                id={id}
                onClick={() => openProfile(id)}
                media={<Avatar customer size="md" name={name} />}
                accessibilityLabel={t("view_details_for", { name })}
                name={name}
              >
                <Text variant="bodyMd" fontWeight="bold" as="h3">
                  {name}
                </Text>
                {/* On a phone the e-mail and the number are one tap away from a letter or a call. */}
                <div>
                  {isPhone && email ? (
                    <a href={`mailto:${email}`} style={linkAboveItem}>
                      {email}
                    </a>
                  ) : (
                    email
                  )}
                </div>
                <div>
                  {isPhone && phone ? (
                    <a href={`tel:${phone}`} style={linkAboveItem}>
                      {phone}
                    </a>
                  ) : (
                    phone
                  )}
                </div>
              </ResourceItem>
            );
          }}
        />
      </LegacyCard>

      {isPhone && matchingUsers.length > visibleCount && (
        <div style={{ marginTop: "10px" }}>
          <Button fullWidth onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}>
            {t("feed_show_more")}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default UserList;
